import AkunSelect from "@/components/app/AkunSelect";
import { useAutoOpen } from "@/hooks/useAutoOpen";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ArrowRightLeft, Gift, Banknote } from "lucide-react";
import { usePabrik } from "@/context/PabrikContext";
import Pagination, { usePagination } from "@/components/app/Pagination";

const kgFmt = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} Kg`;

const ModeToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="transfer-mode">
    {[{ v: false, icon: Gift, t: "Tanpa Uang", d: "Stok pindah dengan nilai HPP" }, { v: true, icon: Banknote, t: "Dengan Uang", d: "Jual internal (pemasukan)" }].map((o) => (
      <button type="button" key={String(o.v)} onClick={() => onChange(o.v)} data-testid={`transfer-mode-${o.v ? "uang" : "gratis"}`}
        className={`text-left rounded-md border px-3 py-2.5 transition-colors duration-150 ${value === o.v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-300"}`}>
        <div className="flex items-center gap-2 text-sm font-medium"><o.icon size={15}/>{o.t}</div>
        <div className={`text-[11px] mt-0.5 ${value === o.v ? "text-slate-300" : "text-slate-500"}`}>{o.d}</div>
      </button>
    ))}
  </div>
);

function TransferDialog({ open, setOpen, item, produk, targets, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (!open) return;
    setF(item
      ? { tanggal: toDateInput(item.tanggal), produk_id: item.produk_id, kg: item.kg, ke_pabrik_id: item.ke_pabrik_id, dengan_uang: !!item.dengan_uang, harga_per_kg: item.harga_per_kg || 0, keterangan: item.keterangan || "" }
      : { tanggal: todayISO(), produk_id: produk[0]?.produk_id || "", kg: "", ke_pabrik_id: targets[0]?.id || "", dengan_uang: false, harga_per_kg: 0, keterangan: "" });
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = produk.find((x) => x.produk_id === f.produk_id);
  const kg = Number(f.kg || 0);
  // Saat edit, stok tersedia = sisa + kg transfer lama (produk sama)
  const tersedia = Number(p?.sisa_stok_kg || 0) + (item && item.produk_id === f.produk_id ? Number(item.kg || 0) : 0);
  const lebih = p && kg > tersedia + 1e-6;
  const nilai = f.dengan_uang ? kg * Number(f.harga_per_kg || 0) : kg * Number(p?.hpp_rata || 0);
  const save = async () => {
    if (!f.produk_id || !f.ke_pabrik_id) { toast.error("Pilih produk & pabrik tujuan"); return; }
    if (!(kg > 0)) { toast.error("Jumlah (Kg) wajib lebih dari 0"); return; }
    if (lebih) { toast.error(`Stok tidak cukup. Tersedia ${kgFmt(tersedia)}`); return; }
    if (f.dengan_uang && !(Number(f.harga_per_kg) > 0)) { toast.error("Isi harga per Kg"); return; }
    try {
      const payload = { ...f, kg, harga_per_kg: Number(f.harga_per_kg || 0) };
      if (item) await api.put(`/transfer-stok/${item.id}`, payload); else await api.post("/transfer-stok", payload);
      toast.success(item ? "Transfer stok diperbarui" : "Transfer stok tersimpan"); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="transfer-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightLeft size={18}/>{item ? "Ubah" : ""} Transfer Stok</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><ModeToggle value={f.dengan_uang} onChange={(v) => setF({ ...f, dengan_uang: v })}/></div>
          <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={f.tanggal || ""} onChange={(e) => setF({ ...f, tanggal: e.target.value })} data-testid="transfer-tanggal"/></div>
          <div className="space-y-1.5"><Label>Ke Pabrik</Label>
            <Select value={f.ke_pabrik_id || ""} onValueChange={(v) => setF({ ...f, ke_pabrik_id: v })}>
              <SelectTrigger data-testid="transfer-ke"><SelectValue placeholder="Pilih pabrik"/></SelectTrigger>
              <SelectContent>{targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.nama}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Produk</Label>
            <Select value={f.produk_id || ""} onValueChange={(v) => setF({ ...f, produk_id: v })}>
              <SelectTrigger data-testid="transfer-produk"><SelectValue placeholder="Pilih produk"/></SelectTrigger>
              <SelectContent>{produk.map((x) => <SelectItem key={x.produk_id} value={x.produk_id}>{x.nama}</SelectItem>)}</SelectContent>
            </Select>
            {p && <div className={`text-[11px] ${lebih ? "text-rose-600" : "text-slate-500"}`} data-testid="transfer-stok-info">Tersedia {kgFmt(tersedia)} · HPP {rupiah(p.hpp_rata)}/Kg</div>}
          </div>
          <div className="space-y-1.5"><Label>Jumlah (Kg)</Label><Input type="number" min="0" step="0.01" value={f.kg ?? ""} className={lebih ? "border-rose-400" : ""} onChange={(e) => setF({ ...f, kg: e.target.value })} data-testid="transfer-kg"/></div>
          {f.dengan_uang && <div className="md:col-span-2 space-y-1.5"><Label>Harga per Kg (Rp)</Label><CurrencyInput value={f.harga_per_kg} onValueChange={(v) => setF({ ...f, harga_per_kg: v })} data-testid="transfer-harga"/></div>}
          {f.dengan_uang && <AkunSelect label="Uang diterima ke akun (pabrik ini)" value={f.akun_id} onChange={(v) => setF((x) => ({ ...x, akun_id: v }))} testid="transfer-akun"/>}
          {f.dengan_uang && <AkunSelect label="Dibayar dari akun (pabrik tujuan)" pabrikId={f.ke_pabrik_id} value={f.akun_id_pembeli} onChange={(v) => setF((x) => ({ ...x, akun_id_pembeli: v }))} testid="transfer-akun-pembeli"/>}
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={f.keterangan || ""} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="transfer-keterangan"/></div>
          <div className="md:col-span-2 rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="transfer-summary">
            <div className="flex justify-between"><span>{f.dengan_uang ? "Nilai penjualan internal" : "Nilai bahan (HPP)"}</span><b className="font-mono text-slate-900">{rupiah(nilai)}</b></div>
            <div className="mt-1 text-[11px] text-slate-500">{f.dengan_uang
              ? "Tercatat sebagai Pemasukan di pabrik ini dan Pengeluaran bahan di pabrik tujuan (tidak dihitung di laporan Gabungan)."
              : "Tanpa pemasukan/pengeluaran. Stok berkurang di sini, masuk Bahan Masuk pabrik tujuan dengan nilai HPP."}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="transfer-save">Simpan Transfer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TransferStok() {
  const { targetsBriket = [], current } = usePabrik();
  const targets = targetsBriket.filter((t) => t.id !== current?.id);
  const [items, setItems] = useState([]);
  const [produk, setProduk] = useState([]);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const openNew = () => { setEditItem(null); setOpen(true); };
  useAutoOpen(openNew);
  const openEdit = (it) => { setEditItem(it); setOpen(true); };
  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/transfer-stok"), api.get("/dashboard/arang")]);
      setItems(a.data); setProduk(b.data.per_produk || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const del = async (it) => {
    if (!window.confirm("Hapus transfer ini? Stok kembali ke pabrik ini.")) return;
    try { await api.delete(`/transfer-stok/${it.id}`); toast.success("Transfer dihapus"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(items, 10);
  return (<>
    <PageHeader title="Transfer Stok" subtitle="Kirim stok ke pabrik lain, tanpa uang atau dengan transaksi uang."
      actions={<Button onClick={openNew} disabled={!targets.length} className="brand-btn rounded-sm" data-testid="add-transfer-stok-btn"><Plus size={14} className="mr-1"/>Transfer</Button>}/>
    <div className="p-4 md:p-8">
      {!targets.length && <div className="card-flat p-4 mb-4 text-sm text-slate-500">Belum ada pabrik Briket aktif sebagai tujuan.</div>}
      <div className="md:hidden space-y-2" data-testid="transfer-cards">
        {items.length === 0 && <div className="card-flat p-6 text-center text-sm text-slate-400">Belum ada transfer.</div>}
        {paged.map((it) => (
          <div key={it.id} className="card-flat p-3 flex items-start gap-3" data-testid={`transfer-card-${it.id}`}>
            <span className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${it.dengan_uang ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{it.dengan_uang ? <Banknote size={15}/> : <Gift size={15}/>}</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate">{it.produk_nama} · {kgFmt(it.kg)}</div>
              <div className="text-[11px] text-slate-500">{tanggalID(it.tanggal)} → {it.ke_pabrik_nama}</div>
              <div className="text-xs font-mono mt-0.5">{rupiah(it.nilai)}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(it)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-transfer-m-${it.id}`}><Pencil size={14}/></button>
              <button onClick={() => del(it)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-transfer-m-${it.id}`}><Trash2 size={14} className="text-rose-600"/></button>
            </div>
          </div>
        ))}
      </div>
      <div className="card-flat overflow-hidden hidden md:block">
        <table className="w-full text-sm" data-testid="transfer-table">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="p-3">Tanggal</th><th className="p-3">Produk</th><th className="p-3">Ke</th><th className="p-3">Mode</th>
            <th className="p-3 text-right">Jumlah</th><th className="p-3 text-right">Nilai</th><th className="p-3 text-right">Aksi</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Belum ada transfer.</td></tr>}
            {paged.map((it) => (
              <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`transfer-row-${it.id}`}>
                <td className="p-3 whitespace-nowrap">{tanggalID(it.tanggal)}</td>
                <td className="p-3 font-medium text-slate-800">{it.produk_nama}</td>
                <td className="p-3">{it.ke_pabrik_nama}</td>
                <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${it.dengan_uang ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`transfer-mode-badge-${it.id}`}>{it.dengan_uang ? `Dengan uang · ${rupiah(it.harga_per_kg)}/Kg` : "Tanpa uang"}</span></td>
                <td className="p-3 text-right font-mono">{kgFmt(it.kg)}</td>
                <td className="p-3 text-right font-mono">{rupiah(it.nilai)}</td>
                <td className="p-3"><div className="flex items-center gap-1 justify-end">
                  <button onClick={() => openEdit(it)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-transfer-${it.id}`}><Pencil size={14}/></button>
                  <button onClick={() => del(it)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-transfer-${it.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="transfer-pagination"/>
    </div>
    <TransferDialog open={open} setOpen={setOpen} item={editItem} produk={produk} targets={targets} onSaved={load}/>
  </>);
}
